'use client';

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useUpdateStrategyMetricMutation } from 'src/api/redux/strategy';
import { formatExecutionParams } from '../MetricItem/formatExecutionParams';

type ColumnItem = {
  id?: string | number;
  label: string;
  description: string;
  disabled?: boolean;
  execution_params?: Record<string, any>;
  summary?: string;
};

interface EditMetricItemProps {
  item: ColumnItem;
  strategyId: string;
  statusLabelMap?: Map<string, string>;
  onCancel: () => void;
  onSave?: () => void;
}

const EditMetricItem: React.FC<EditMetricItemProps> = ({
  item,
  strategyId,
  statusLabelMap,
  onCancel,
  onSave,
}) => {
  const [editValue, setEditValue] = useState(item.label);
  const [updateMetric, { isLoading }] = useUpdateStrategyMetricMutation();

  useEffect(() => {
    setEditValue(item.label);
  }, [item.label]);

  const handleSave = async () => {
    if (!editValue.trim()) {
      onCancel();
      return;
    }

    if (editValue === item.label) {
      onCancel();
      return;
    }

    if (!item.id || item.disabled) {
      onCancel();
      return;
    }

    try {
      await updateMetric({
        metricId: item.id?.toString(),
        strategyId: strategyId,
        body: {
          label: editValue.trim(),
        },
      }).unwrap();

      onSave?.();
      onCancel();
    } catch (error) {
      console.error('Failed to update metric:', error);
      setEditValue(item.label); // Reset to original on error
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const hasChanges = editValue.trim() !== item.label;
  const testId = `edit-metric-item-${
    item.id || item.label.toLowerCase().replace(/\s+/g, '-')
  }`;

  return (
    <li
      className="group grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4"
      data-testid={testId}
      aria-label={`Editing metric: ${item.label}`}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="h-9 px-3 py-2 rounded-full outline outline-1 -outline-offset-1 outline-blue-500 bg-common-White flex items-center flex-1 min-w-0">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="flex-1 bg-transparent border-none outline-none text-sm font-normal font-['Inter'] leading-tight text-primary truncate"
            autoFocus
            aria-label={`Edit label for ${item.label}`}
            data-testid={`${testId}-input`}
          />
        </div>
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Edit actions"
        >
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200"
            aria-label="Cancel editing"
            data-testid={`${testId}-cancel-button`}
          >
            <Icon icon={faXmark} className="w-4 h-4" />
          </button>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={isLoading || !editValue.trim()}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-full transition-all duration-200 disabled:opacity-50"
              aria-label="Save changes"
              data-testid={`${testId}-save-button`}
            >
              <Icon icon={faCheck} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <p
        className="text-secondary text-xs font-normal font-['Inter'] leading-none flex items-center"
        data-testid={`${testId}-description`}
      >
        {formatExecutionParams(item.execution_params, statusLabelMap) || item.description}
      </p>
    </li>
  );
};

export default EditMetricItem;
